import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Button, InputNumber, Form, notification, Spin, Table, Space, Input } from 'antd';
import { GestionPaieService } from "../Services/GestionPaieService";
import moment from 'moment';
import { PrinterOutlined, UnorderedListOutlined } from '@ant-design/icons';


const EditPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [calculatedData, setCalculatedData] = useState([]);
  const [initialRecord, setInitialRecord] = useState(null);
  const [form] = Form.useForm();
  const [isSubmitted, setIsSubmitted] = useState(false); // Nouvel état pour gérer l'affichage des boutons
  const gestionPaieService = new GestionPaieService();
  const record = location.state?.record;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await gestionPaieService.getRubriquesSaisie(record.numbull, record.matrisal);
      const formattedData = data.map(item => ({ ...item }));
      setTableData(formattedData);
      const formFields = formattedData.reduce((acc, item) => {
        acc[`valRub_${item.codeRub}`] = item.valRub;
        return acc;
      }, {});
      form.setFieldsValue({ ...record, ...formFields });
      setInitialRecord(record);
    } catch (error) {
      console.error("Erreur lors de la récupération des données :", error);
      notification.error({
        message: 'Erreur',
        description: "Erreur lors de la récupération des données."
      });
    } finally {
      setLoading(false);
    }
  }, [form, record]);

  useEffect(() => {
    if (!record) {
      navigate('/gestion-paie');
      notification.error({
        message: 'Erreur',
        description: "Les données du bulletin sont manquantes."
      });
      return;
    }

    fetchData();
  }, [fetchData, navigate, record]);

  const handleValuesChange = (changedValues) => {
    if (changedValues[`valRub_2`] !== undefined) {
      const joursAbsents = changedValues[`valRub_2`];
      const heuresNormales = 173.33 - joursAbsents * 8;
      form.setFieldsValue({ [`valRub_3`]: heuresNormales });
    }
  };

  const fetchCalculatedData = useCallback(async (numbull) => {
    setLoading(true);
    try {
      const data = await gestionPaieService.getBullRubCal(numbull);
      setCalculatedData(data);
    } catch (error) {
      console.error("Erreur lors de la récupération des données calculées :", error);
      notification.error({
        message: 'Erreur',
        description: "Erreur lors de la récupération des données calculées."
      });
    } finally {
      setLoading(false);
    }
  }, []);

 

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const values = form.getFieldsValue();
      const updatedData = tableData.map(item => ({
        ...item,
        valRub: values[`valRub_${item.codeRub}`]
      }));

      await gestionPaieService.addBullRub(updatedData);

      notification.success({
        message: 'Succès',
        description: "Bulletin calculé avec succès."
      });

      await fetchCalculatedData(record.numbull);
      setIsSubmitted(true); // Mettre à jour l'état pour afficher le nouveau bouton

    } catch (error) {
      console.error("Erreur lors de la mise à jour des données :", error);
      notification.error({
        message: 'Erreur',
        description: "Erreur lors de la mise à jour des données."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setIsSubmitted(false);
  };

  const handlePrintPDF = useCallback((numbull) => {
    gestionPaieService.printBulletinsService(numbull);
  }, [gestionPaieService]);

  if (!initialRecord) {
    return <Spin />;
  }

  const columns = [
    {
      title: 'Code',
      dataIndex: 'codeRub',
      key: 'codeRub',
      align: 'center'
    },
    {
      title: 'Rubrique',
      dataIndex: 'intitule',
      key: 'intitule',
    },
    {
      title: 'Valeur',
      dataIndex: 'valRub',
      key: 'valRub',
      //align: 'right',
      render: (text, record) => (
        <Form.Item name={`valRub_${record.codeRub}`} className="right-align-input">
          <InputNumber style={{ width: '100%' }} />
        </Form.Item>
      ),
    },
  ];

  const formatNumberWithSeparator = (number) => {
    if (!number && number !== 0) return '';
  
    // Arrondir à l'entier le plus proche
    const roundedNumber = Math.round(number);
  
    // Utiliser toLocaleString pour formater avec des séparateurs de milliers
    return roundedNumber.toLocaleString('fr-FR');
  };
  
  

  const renderValue = (value) => (value === 0 ? '' : formatNumberWithSeparator(value));

  const calculatedColumns = [
    { title: 'Code', dataIndex: 'coderub', key: 'coderub' },
    { title: 'Rubrique', dataIndex: 'intitule', key: 'intitule' },
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre', align: 'right', render: renderValue },
    {
      title: 'Base',
      dataIndex: 'base',
      key: 'base',
      align: 'right',
      render: renderValue, // Afficher et formater les bases
    },
    { title: 'Taux Salariale', dataIndex: 'txsal', key: 'txsal', align: 'right', render: renderValue },
    { title: 'Gain', dataIndex: 'gain', key: 'gain', align: 'right', render: renderValue },
    { title: 'Retenue', dataIndex: 'retenue', key: 'retenue', align: 'right', render: renderValue },
    { title: 'Taux PAT.', dataIndex: 'txpat', key: 'txpat', align: 'right', render: renderValue },
    { title: 'Cotis. PAT.', dataIndex: 'cotispat', key: 'cotispat', align: 'right', render: renderValue },
  ];
  
  return (
    <Card
      title={
        <div>
          <div>{`${initialRecord.matrisal} : ${initialRecord.prenoms} ${initialRecord.nom} - ${isSubmitted ? 'Bulletin Calculé' : 'Saisie des Valeurs de Base'}`}</div>
          <strong>{`Période du ${moment(initialRecord.debut).format("D MMMM YYYY")} au ${moment(initialRecord.fin).format("D MMMM YYYY")}`}</strong>
        </div>
      }
      extra={
        <>
          <Space>
            {isSubmitted && (
              <Button type="primary" icon={<PrinterOutlined />} onClick={() => handlePrintPDF(record.numbull)}
                aria-label={`Imprimer le bulletin de ${record.nom} ${record.prenoms}`} >
                Imprimer
              </Button>
            )}
            <Button onClick={() => navigate("/gestion-paie")} type="primary" icon={<UnorderedListOutlined />}>
              Liste des Bulletins
            </Button>
            {isSubmitted && (
              <Button type="primary" onClick={handleReset}>
                Saisie Bulletin
              </Button>
            )}
          </Space>
        </>
      }
    >
      {loading ? (
        <Spin />
      ) : (
        <>
          <Form form={form} onFinish={handleSubmit} onValuesChange={handleValuesChange} layout="vertical">
            {isSubmitted ? (
              <Table
                scroll={{ x: true }}
                style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
                className="custom-header1-table"
                
                rowClassName={() => 'editable-row'}
                columns={calculatedColumns}
                showSorterTooltip={false}
                dataSource={calculatedData}
                pagination={{ pageSize: 15 }}
              />
            ) : (
              <Table
              scroll={{ y: 550 }} // Ajoutez cette ligne pour le défilement vertical avec une hauteur de 400px
              style={{ cursor: "pointer", whiteSpace: "nowrap", width: "100%" }}
              className="custom-header1-table"
              showSorterTooltip={false}
              columns={columns}
              dataSource={tableData}
              pagination={false} // Supprimez la pagination
            />
            
            )}
            {!isSubmitted && (
              <Form.Item style={{ textAlign: 'right' }}>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Bulletin Calculé
                </Button>
              </Form.Item>
            )}
          </Form>
        </>
      )}
    </Card>
  );
};

export default EditPage;
